import { ArrowClockwise, Star } from '@phosphor-icons/react'
import {
  useSystemStatus,
  toStatusDotStatus,
  formatAgentVertical,
  formatFlagName,
} from '@/hooks/useSystemStatus'
import { StatusDot } from '@/components/StatusDot'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

// ---------- Helpers ----------

function timeAgo(epochMs: number): string {
  const diffMs = Date.now() - epochMs
  const diffSec = Math.floor(diffMs / 1000)

  if (diffSec < 5) return 'just now'
  if (diffSec < 60) return `${diffSec}s ago`

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin === 1) return '1 min ago'
  if (diffMin < 60) return `${diffMin} min ago`

  const diffHours = Math.floor(diffMin / 60)
  if (diffHours === 1) return '1 hour ago'
  return `${diffHours} hours ago`
}

// ---------- Infrastructure Service Card ----------

function InfraServiceCard({
  label,
  status,
  latencyMs,
  details,
}: {
  label: string
  status: string
  latencyMs?: number
  details?: Array<{ label: string; value: string }>
}) {
  return (
    <Card className="flex-1 min-w-[200px]">
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 mb-2">
          <StatusDot status={toStatusDotStatus(status)} />
          <span className="text-sm font-semibold">{label}</span>
        </div>
        {latencyMs !== undefined && (
          <p className="text-xs text-muted-foreground">{latencyMs}ms latency</p>
        )}
        {details?.map((d) => (
          <p key={d.label} className="text-xs text-muted-foreground">
            {d.label}: <span className="font-medium text-foreground">{d.value}</span>
          </p>
        ))}
      </CardContent>
    </Card>
  )
}

function InfraServiceCardSkeleton() {
  return (
    <Card className="flex-1 min-w-[200px]">
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="h-2.5 w-2.5 rounded-full" />
          <Skeleton className="h-4 w-20" />
        </div>
        <Skeleton className="h-3 w-24 mt-1" />
        <Skeleton className="h-3 w-32 mt-1" />
      </CardContent>
    </Card>
  )
}

// ---------- System Page ----------

export function System() {
  const { data, isLoading, dataUpdatedAt, isRefetchError, refetch, isFetching } =
    useSystemStatus()

  // Compute earliest next job run for Scheduler card
  const schedulerData = data?.infrastructure.scheduler
  const nextJobRun = schedulerData?.jobs
    .map((j) => j.next_run)
    .filter((r): r is string => r !== null)
    .sort()[0] // ISO strings sort correctly lexicographically
  const nextJobDisplay = nextJobRun
    ? `Next: ${timeAgo(new Date(nextJobRun).getTime())}`
    : 'No jobs scheduled'

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">System</h2>
          <p className="text-sm text-muted-foreground">
            Platform diagnostic dashboard
          </p>
        </div>
        <div className="flex items-center gap-3">
          {dataUpdatedAt > 0 && (
            <span className="text-xs text-muted-foreground">
              Updated {timeAgo(dataUpdatedAt)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowClockwise
              className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stale data warning banner */}
      {isRefetchError && !!data && (
        <div className="rounded-md border border-yellow-500/50 bg-yellow-500/10 px-4 py-2 text-sm text-yellow-700 dark:text-yellow-400">
          Data may be stale — last successful refresh was{' '}
          {timeAgo(dataUpdatedAt)}.
        </div>
      )}

      {/* Infrastructure Health */}
      <div>
        <h3 className="text-base font-semibold mb-3">Infrastructure Health</h3>
        <div className="flex flex-wrap gap-3">
          {isLoading ? (
            <>
              <InfraServiceCardSkeleton />
              <InfraServiceCardSkeleton />
              <InfraServiceCardSkeleton />
              <InfraServiceCardSkeleton />
            </>
          ) : data ? (
            <>
              <InfraServiceCard
                label="Database"
                status={data.infrastructure.database.status}
                latencyMs={data.infrastructure.database.latency_ms}
                details={[{ label: 'Migration', value: data.database_state.alembic_version }]}
              />
              <InfraServiceCard
                label="Redis (Cache)"
                status={data.infrastructure.redis.status}
                latencyMs={data.infrastructure.redis.latency_ms}
                details={[{
                  label: 'Status',
                  value: data.infrastructure.redis.status === 'unconfigured' ? 'Unconfigured' : 'Configured',
                }]}
              />
              <InfraServiceCard
                label="Weaviate (Vector DB)"
                status={data.infrastructure.weaviate.status}
                latencyMs={data.infrastructure.weaviate.latency_ms}
                details={[{
                  label: 'Status',
                  value: data.infrastructure.weaviate.status === 'unconfigured' ? 'Unconfigured' : 'Configured',
                }]}
              />
              <InfraServiceCard
                label="Scheduler"
                status={data.infrastructure.scheduler.status}
                details={[
                  { label: 'Jobs', value: String(data.infrastructure.scheduler.job_count) },
                  { label: nextJobRun ? 'Next run' : 'Jobs', value: nextJobDisplay },
                ]}
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Agent Registry */}
      <div>
        <h3 className="text-base font-semibold mb-3">Agent Registry</h3>
        {isLoading ? (
          <Card>
            <CardContent className="pt-4">
              <Skeleton className="h-8 w-full mb-2" />
              <Skeleton className="h-8 w-full mb-2" />
              <Skeleton className="h-8 w-full mb-2" />
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
        ) : data ? (
          data.agents.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No agents registered
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-0 px-0 pb-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent ID</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Vertical</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Prompt Version</TableHead>
                      <TableHead>Default</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.agents.map((agent) => (
                      <TableRow key={agent.agent_id}>
                        <TableCell className="font-mono text-xs">
                          {agent.agent_id}
                        </TableCell>
                        <TableCell className="text-xs">{agent.model}</TableCell>
                        <TableCell className="text-xs">
                          {formatAgentVertical(agent.agent_id)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={agent.enabled ? 'default' : 'secondary'}>
                            {agent.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {agent.prompt_version}
                        </TableCell>
                        <TableCell>
                          {agent.is_default && (
                            <Badge variant="outline" className="gap-1">
                              <Star className="h-3 w-3" weight="fill" />
                              Default
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        ) : null}
      </div>

      {/* Feature Flags */}
      <div>
        <h3 className="text-base font-semibold mb-3">Feature Flags</h3>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="px-4 py-3">
                <Skeleton className="h-3 w-24 mb-2" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </Card>
            ))}
          </div>
        ) : data ? (
          Object.keys(data.feature_flags).length === 0 ? (
            <p className="text-sm text-muted-foreground">No feature flags configured</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {Object.entries(data.feature_flags).map(([key, value]) => (
                <Card key={key} className="px-4 py-3">
                  <p className="text-xs text-muted-foreground mb-1.5">{formatFlagName(key)}</p>
                  <Badge
                    variant={value ? 'default' : 'secondary'}
                    className={value ? 'bg-success text-success-foreground border-transparent' : ''}
                  >
                    {value ? 'Enabled' : 'Disabled'}
                  </Badge>
                </Card>
              ))}
            </div>
          )
        ) : null}
      </div>

      {/* Database State */}
      <div>
        <h3 className="text-base font-semibold mb-3">Database State</h3>
        {isLoading ? (
          <Card>
            <CardContent className="pt-4">
              <Skeleton className="h-5 w-48 mb-3" />
              <Skeleton className="h-8 w-full mb-2" />
              <Skeleton className="h-8 w-full mb-2" />
              <Skeleton className="h-8 w-full mb-2" />
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
        ) : data ? (
          <Card>
            <CardContent className="pt-4">
              {/* Top: Alembic version + RLS summary */}
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className="text-sm text-muted-foreground">Migration:</span>
                <Badge variant="outline" className="font-mono text-xs">
                  {data.database_state.alembic_version}
                </Badge>
                <span className="text-sm text-muted-foreground ml-1">
                  {data.database_state.rls_policies} RLS {data.database_state.rls_policies === 1 ? 'policy' : 'policies'} on
                </span>
                {data.database_state.rls_tables.length > 0 ? (
                  data.database_state.rls_tables.map(t => (
                    <Badge key={t} variant="secondary" className="font-mono text-xs">{t}</Badge>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">no tables</span>
                )}
              </div>

              {/* Bottom: Row counts table */}
              {Object.keys(data.database_state.row_counts).length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Table</TableHead>
                      <TableHead className="text-right">Approx. Rows</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(data.database_state.row_counts)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([table, count]) => (
                        <TableRow key={table}>
                          <TableCell className="font-mono text-xs">{table}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums">
                            {count.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                  <TableCaption className="text-xs">
                    Row counts are approximate estimates from PostgreSQL statistics (pg_class.reltuples).
                  </TableCaption>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">No row count data available</p>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  )
}
